/**
 * Driver-side preference for which GPS app the "Slide to start trip" button
 * opens. Stored in localStorage so it travels with the device, not the user
 * account. Supported values:
 *   - "google" (default) : universal `https://www.google.com/maps/dir/?…`
 *   - "waze"             : `https://waze.com/ul?q=…&navigate=yes`
 *   - "apple"            : `maps://?daddr=…` (iOS-only; falls back to Google)
 */
const KEY = "saor_gps_pref";

export const GPS_OPTIONS = [
  { id: "google", label: "Google Maps" },
  { id: "waze", label: "Waze" },
  { id: "apple", label: "Apple Plans" },
];

export function getGpsPref() {
  try {
    const v = (localStorage.getItem(KEY) || "google").toLowerCase();
    return GPS_OPTIONS.some((o) => o.id === v) ? v : "google";
  } catch (_) {
    return "google";
  }
}

export function setGpsPref(value) {
  try {
    localStorage.setItem(KEY, value);
  } catch (_) {
    /* private mode / no storage — silently ignored */
  }
}

function _open(url) {
  try {
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } catch (_) {
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

/**
 * Launch the user's preferred GPS app for the given address.
 *   - `address` : full street address string (required).
 *   - `coord`   : `{lat, lng}` for the destination — used by Waze/Apple
 *                  when available since their address parser is weaker.
 */
export function launchGps(address, coord) {
  if (!address && !coord) return;
  const pref = getGpsPref();
  const enc = encodeURIComponent(address || "");
  if (pref === "waze") {
    const url = coord?.lat && coord?.lng
      ? `https://waze.com/ul?ll=${coord.lat}%2C${coord.lng}&navigate=yes`
      : `https://waze.com/ul?q=${enc}&navigate=yes`;
    _open(url);
    return;
  }
  if (pref === "apple") {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent || "");
    if (isIOS) {
      const url = coord?.lat && coord?.lng
        ? `maps://?daddr=${coord.lat},${coord.lng}&dirflg=d`
        : `maps://?daddr=${enc}&dirflg=d`;
      _open(url);
      return;
    }
    // non-iOS → fall through to Google Maps
  }
  // Default: Google Maps (works on iOS, Android, web).
  _open(
    `https://www.google.com/maps/dir/?api=1&destination=${enc}&travelmode=driving`,
  );
}
